import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { MouseEventHandler, useEffect, useRef } from "react";
import clsx from "clsx";

import { Pencil, Settings, ChevronUp as ArrowIcon } from "lucide-react";
import { PreviewType, usePreview } from "../../hooks/usePreview";

const useStyles = makeStyles((theme) => ({
  header: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(3),
    pointerEvents: "auto",
  },

  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    position: "relative",
  },
  collapsedHeader: {
    marginBottom: 0,
  },

  arrowIconDown: {
    transform: "rotate(180deg)",
  },

  headerIcon: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  headerTextBlock: {
    display: "flex",
    flexDirection: "column",
  },

  title: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: "20px",
    color: theme.palette.text.primary,
  },

  subtitle: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "18px",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },

  todayContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
  },

  today: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: "12px",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.selected,
    borderRadius: 4,
    padding: theme.spacing(0.2, 0.8),
    textTransform: "uppercase",
  },

  todayValue: {
    fontSize: 20,
    fontWeight: 700,
    lineHeight: "24px",
    color: theme.palette.text.primary,
  },

  iconButtonGroup: {
    display: "flex",
    alignItems: "center",
    position: "absolute",
    top: theme.spacing(2),
    right: 0,
    gap: theme.spacing(1),
    pointerEvents: "none",
  },
  iconButton: {
    margin: theme.spacing(-1, 0),
    pointerEvents: "auto",
  },

  arrowIcon: {
    marginLeft: theme.spacing(2),
  },

  arrowIconRight: {
    transform: "rotate(90deg)",
    marginRight: theme.spacing(1),
  },
  editSettingsColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
}));

interface ClientDetailsCardHeaderProps {
  icon: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  todayValue?: string;
  onEditClick?: MouseEventHandler;
  disableOnEdit?: boolean;
  onSettingsClick?: MouseEventHandler;
  disableSettings?: boolean;
  disableCompactEdit?: boolean;
  isOpenContent?: boolean;
  setIsOpenContent?: (value: boolean) => void;
  children?: React.ReactNode;
  showSubtitleSkeleton?: boolean;
  showTodayValueSkeleton?: boolean;
  className?: string;
  isPreview?: boolean;
  isFirst?: boolean;
  collapsable?: boolean;
  defaultOpen?: boolean;
}

export default function ClientDetailsCardHeader(
  props: ClientDetailsCardHeaderProps,
) {
  const {
    icon,
    title,
    subtitle,
    todayValue,
    onEditClick,
    onSettingsClick,
    disableOnEdit,
    disableSettings,
    disableCompactEdit,
    isOpenContent,
    setIsOpenContent,
    children,
    showSubtitleSkeleton,
    showTodayValueSkeleton,
    className,
    isPreview,
    isFirst,
    collapsable = false,
    defaultOpen,
  } = props;

  const s = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);

  const iconSize = collapsable ? 18 : 24;
  const [preview] = usePreview();
  const isAnyPreview =
    preview === PreviewType.MOBILE ||
    preview === PreviewType.TABLET ||
    preview === PreviewType.WEB;

  const canShowCompactEdit =
    onEditClick && !onSettingsClick && !disableCompactEdit;

  useEffect(() => {
    if (setIsOpenContent) {
      setIsOpenContent(!!defaultOpen);
    }
  }, [defaultOpen, setIsOpenContent]);

  return (
    <Box
      ref={containerRef}
      className={clsx(s.header, !isOpenContent && s.collapsedHeader, className)}
      sx={{ cursor: collapsable ? "pointer" : "default" }}
      onClick={() => {
        if (setIsOpenContent && collapsable) {
          setIsOpenContent(!isOpenContent);
        }
      }}
    >
      <Box className={s.topRow}>
        <Box>
          <Box className={s.headerIcon}>
            {icon}
            <Box className={s.headerTextBlock}>
              <Typography variant="h6" className={s.title} children={title} />
              {showSubtitleSkeleton ? (
                <Skeleton width={120} height={20} />
              ) : subtitle ? (
                <Typography
                  variant="body1"
                  className={s.subtitle}
                  children={subtitle}
                />
              ) : null}
            </Box>
          </Box>
          {(showTodayValueSkeleton || todayValue) && (
            <Box className={s.todayContainer}>
              {showTodayValueSkeleton ? (
                <>
                  <Skeleton width={120} height={24} />
                </>
              ) : (
                <>
                  <Typography className={s.todayValue}>{todayValue}</Typography>
                  <Typography className={s.today}>TODAY</Typography>
                </>
              )}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            pointerEvents: isAnyPreview ? "none" : "auto",
            zIndex: 2,
          }}
        >
          {children}
        </Box>

        <Box className={s.iconButtonGroup}>
          {canShowCompactEdit ? (
            <ArrowIcon
              className={clsx(s.arrowIcon, s.arrowIconRight)}
              onClick={(e) => {
                onEditClick(e);
                e.stopPropagation();
              }}
            />
          ) : (
            onEditClick && (
              <IconButton
                disabled={disableOnEdit || isAnyPreview}
                className={s.iconButton}
                onClick={(e) => {
                  onEditClick(e);
                  e.stopPropagation();
                }}
              >
                <Pencil size={iconSize} />
              </IconButton>
            )
          )}
          {onSettingsClick && (
            <IconButton
              disabled={disableSettings || isAnyPreview}
              onClick={(e) => {
                onSettingsClick(e);
                e.stopPropagation();
              }}
              className={s.iconButton}
            >
              <Settings size={iconSize} />
            </IconButton>
          )}
          {setIsOpenContent && collapsable && (
            <IconButton
              onClick={(e) => {
                setIsOpenContent(!isOpenContent);
                e.stopPropagation();
              }}
              className={clsx(s.iconButton)}
            >
              <ArrowIcon
                size={24}
                className={clsx(!isOpenContent && s.arrowIconDown)}
              />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
}
